/**
 * API error codes dictionary. Aligns with backend docs/API_ERROR_CODES.md.
 * Use for branching in error handlers and for consistent toast messages.
 *
 * Message strings MUST match backend src/common/errors/dictionaries/*.ts so we do not
 * invent copy. Exception: USER_NOT_FOUND uses a generic auth message to avoid leaking
 * whether an account exists (security).
 */

/** Runtime object of error codes (enum-like). Use for comparisons: error.code === ApiErrorCode.TEMPORARY_LOCKOUT */
export const ApiErrorCode = {
  // Auth
  AUTHENTICATION_RECONFIRMATION_REQUIRED: "AUTHENTICATION_RECONFIRMATION_REQUIRED",
  AUTH_TURNSTILE_VERIFICATION_FAILED: "AUTH_TURNSTILE_VERIFICATION_FAILED",
  INVALID_TOTP_TOKEN: "INVALID_TOTP_TOKEN",
  LEGACY_AUTH_ROUTE_DISABLED: "LEGACY_AUTH_ROUTE_DISABLED",
  PASSKEY_REGISTRATION_CANCELLED: "PASSKEY_REGISTRATION_CANCELLED",
  PASSKEY_VERIFICATION_FAILED: "PASSKEY_VERIFICATION_FAILED",
  SESSION_EXPIRED: "SESSION_EXPIRED",
  TEMPORARY_LOCKOUT: "TEMPORARY_LOCKOUT",
  /** Social sign-in refused because the account was created with another method. */
  INVALID_LOGIN_METHOD: "INVALID_LOGIN_METHOD",
  // User – email verification / general
  EMAIL_ALREADY_VERIFIED: "EMAIL_ALREADY_VERIFIED",
  RATE_LIMITED: "RATE_LIMITED",
  USER_NOT_FOUND: "USER_NOT_FOUND",
  USER_ALREADY_VERIFIED: "USER_ALREADY_VERIFIED",
  ACCOUNT_ALREADY_ACTIVE: "ACCOUNT_ALREADY_ACTIVE",
  ACCOUNT_ALREADY_DEACTIVATED: "ACCOUNT_ALREADY_DEACTIVATED",
  ACCOUNT_DEACTIVATED: "ACCOUNT_DEACTIVATED",
  ACCOUNT_PENDING_DELETION: "ACCOUNT_PENDING_DELETION",
  SSO_REQUIRED_FOR_DOMAIN: "SSO_REQUIRED_FOR_DOMAIN",
  CANNOT_ACTIVATE_FREE_PLAN: "CANNOT_ACTIVATE_FREE_PLAN",
  CANNOT_DEACTIVATE_FREE_PLAN: "CANNOT_DEACTIVATE_FREE_PLAN",
  INVITATION_NOT_FOUND: "INVITATION_NOT_FOUND",
  // User – email change
  ALREADY_VERIFIED: "ALREADY_VERIFIED",
  CODE_EXPIRED: "CODE_EXPIRED",
  EMAIL_ALREADY_EXISTS: "EMAIL_ALREADY_EXISTS",
  INVALID_CODE: "INVALID_CODE",
  INVALID_PASSWORD: "INVALID_PASSWORD",
  NO_PASSWORD_SET: "NO_PASSWORD_SET",
  NO_PENDING_REQUEST: "NO_PENDING_REQUEST",
  PENDING_EMAIL_CHANGE_EXISTS: "PENDING_EMAIL_CHANGE_EXISTS",
  // User – invitations / workspace
  INVITATION_DUPLICATE: "INVITATION_DUPLICATE",
  INVITE_USER_MISMATCH: "INVITE_USER_MISMATCH",
  NO_OPEN_SEAT: "NO_OPEN_SEAT",
  SELF_INVITE: "SELF_INVITE",
  INVITATION_NOT_PENDING: "INVITATION_NOT_PENDING",
  INVITATION_EXPIRED: "INVITATION_EXPIRED",
  INVALID_INVITATION_TOKEN: "INVALID_INVITATION_TOKEN",
  CANNOT_REMOVE_OWNER: "CANNOT_REMOVE_OWNER",
  WORKSPACE_OWNER_MEMBERSHIP_LOCKED: "WORKSPACE_OWNER_MEMBERSHIP_LOCKED",
  WORKSPACE_MEMBER_SELF_CHANGE_NOT_ALLOWED: "WORKSPACE_MEMBER_SELF_CHANGE_NOT_ALLOWED",
  WORKSPACE_ADMIN_CHANGE_REQUIRES_OWNER: "WORKSPACE_ADMIN_CHANGE_REQUIRES_OWNER",
  WORKSPACE_MEMBER_CHANGE_NOT_ALLOWED: "WORKSPACE_MEMBER_CHANGE_NOT_ALLOWED",
  WORKSPACE_ROLE_GRANT_NOT_ALLOWED: "WORKSPACE_ROLE_GRANT_NOT_ALLOWED",
  PROJECT_CREATOR_ROLE_CANNOT_ASSIGN: "PROJECT_CREATOR_ROLE_CANNOT_ASSIGN",
  OWNER_ROLE_CANNOT_ASSIGN: "OWNER_ROLE_CANNOT_ASSIGN",
  PROJECT_NOT_IN_WORKSPACE: "PROJECT_NOT_IN_WORKSPACE",
  WORKSPACE_ADMIN_REQUIRED: "WORKSPACE_ADMIN_REQUIRED",
  WORKSPACE_NAME_EMPTY: "WORKSPACE_NAME_EMPTY",
  WORKSPACE_MEMBER_LIMIT_REACHED: "WORKSPACE_MEMBER_LIMIT_REACHED",
  NOT_ORGANIZATION_MEMBER: "NOT_ORGANIZATION_MEMBER",
  PRIMARY_ACCOUNT_ALREADY_REGISTERED: "PRIMARY_ACCOUNT_ALREADY_REGISTERED",
  PROJECT_ACCESS_REQUIRED: "PROJECT_ACCESS_REQUIRED",
  PROJECT_REQUIRED: "PROJECT_REQUIRED",
  PROJECT_TEAM_LIMIT_REACHED: "PROJECT_TEAM_LIMIT_REACHED",
  PROJECT_SEAT_CAP_REACHED: "PROJECT_SEAT_CAP_REACHED",
  USER_ALREADY_ACTIVE: "USER_ALREADY_ACTIVE",
  USER_ALREADY_MEMBER: "USER_ALREADY_MEMBER",
  USER_ALREADY_REVOKED: "USER_ALREADY_REVOKED",
  PENDING_INVITATION_EXISTS: "PENDING_INVITATION_EXISTS",
  INVALID_EMAIL_DOMAIN: "INVALID_EMAIL_DOMAIN",
  CANNOT_EXTEND_PLAN: "CANNOT_EXTEND_PLAN",
  NO_SUBSCRIPTION: "NO_SUBSCRIPTION",
  INVALID_EXPIRY_DATE: "INVALID_EXPIRY_DATE",
  SEAT_LIMIT_REACHED: "SEAT_LIMIT_REACHED",
  // Project
  PROJECT_LIMIT_REACHED: "PROJECT_LIMIT_REACHED",
  PROJECT_NOT_FOUND: "PROJECT_NOT_FOUND",
  NO_FILE_UPLOADED: "NO_FILE_UPLOADED",
  NO_PRICING_PLAN: "NO_PRICING_PLAN",
  UNLICENSED_MAX_DRAFT_SCENES_REACHED: "UNLICENSED_MAX_DRAFT_SCENES_REACHED",
  // Workspace
  WORKSPACE_NOT_FOUND: "WORKSPACE_NOT_FOUND",
  NOT_WORKSPACE_MEMBER: "NOT_WORKSPACE_MEMBER",
  CLAUDE_CONNECTOR_LICENSE_REQUIRED: "CLAUDE_CONNECTOR_LICENSE_REQUIRED",
  MCP_BASE_URL_NOT_CONFIGURED: "MCP_BASE_URL_NOT_CONFIGURED",
  // Payment / subscription
  CUSTOMER_ID_OR_EMAIL_REQUIRED: "CUSTOMER_ID_OR_EMAIL_REQUIRED",
  NOT_ENOUGH_PROJECTS: "NOT_ENOUGH_PROJECTS",
  NOT_ENOUGH_SEATS: "NOT_ENOUGH_SEATS",
  SUBSCRIPTION_ALREADY_CANCELLED: "SUBSCRIPTION_ALREADY_CANCELLED",
  INSUFFICIENT_AI_CREDITS: "INSUFFICIENT_AI_CREDITS",
  // Budgeting / scheduling
  BUDGET_RERUN_DISALLOWED: "BUDGET_RERUN_DISALLOWED",
  BUDGET_RERUN_IN_PROGRESS: "BUDGET_RERUN_IN_PROGRESS",
  BUDGET_VERSION_LIMIT_REACHED: "BUDGET_VERSION_LIMIT_REACHED",
  BUDGET_INVALID_ACCOUNT_NUMBER: "BUDGET_INVALID_ACCOUNT_NUMBER",
  BUDGET_DUPLICATE_ACCOUNT_NUMBER: "BUDGET_DUPLICATE_ACCOUNT_NUMBER",
  BUDGET_LOCKED: "BUDGET_LOCKED",
  BUDGET_RESTORE_BLOCKED_BY_ACTUALS: "BUDGET_RESTORE_BLOCKED_BY_ACTUALS",
  BUDGET_RESTORE_BLOCKED_WHEN_LOCKED: "BUDGET_RESTORE_BLOCKED_WHEN_LOCKED",
  DAYBREAK_LOCKED: "DAYBREAK_LOCKED",
  STRIPBOARD_ARCHIVED: "STRIPBOARD_ARCHIVED",
  STRIPBOARD_INDEX_TAKEN: "STRIPBOARD_INDEX_TAKEN",
  BUDGET_STRIPBOARD_SHOOT_TIME_UNAVAILABLE: "BUDGET_STRIPBOARD_SHOOT_TIME_UNAVAILABLE",
  // Tax
  ERROR_ADDING_TAX_INCENTIVE_TYPE: "ERROR_ADDING_TAX_INCENTIVE_TYPE",
  ERROR_CREATING_TAX_INCENTIVE: "ERROR_CREATING_TAX_INCENTIVE",
  ERROR_FETCHING_TAX_INCENTIVE: "ERROR_FETCHING_TAX_INCENTIVE",
  ERROR_FETCHING_TAX_INCENTIVE_TYPES: "ERROR_FETCHING_TAX_INCENTIVE_TYPES",
  // Other
  AI_API_ERROR: "AI_API_ERROR",
  INTERNAL_SERVER_ERROR: "INTERNAL_SERVER_ERROR",
  MAINTENANCE: "MAINTENANCE",
} as const;

export type ApiErrorCodeType = (typeof ApiErrorCode)[keyof typeof ApiErrorCode];

/** Fallback message when no code-specific or API message is available. */
export const GENERIC_SERVER_ERROR_MESSAGE = "Uh oh, something went wrong. Please try again later.";

/**
 * Toast message for each error code. Used by getToastMessageForError().
 * Messages MUST match backend src/common/errors/dictionaries/*.ts (single source of truth).
 * Codes not in this map fall back to error.message or GENERIC_SERVER_ERROR_MESSAGE.
 *
 * Deliberate exceptions, where the app text differs from the API text on purpose:
 * - USER_NOT_FOUND: generic sign-in text, so the toast does not reveal whether an account exists.
 * - BUDGET_STRIPBOARD_SHOOT_TIME_UNAVAILABLE: says "shoot day", the app's term for what the
 *   API calls a day break.
 */
export const ERROR_CODE_TOAST_MESSAGES: Partial<Record<ApiErrorCodeType, string>> = {
  // Auth (auth.dictionary.ts)
  [ApiErrorCode.AUTHENTICATION_RECONFIRMATION_REQUIRED]:
    "For your security, sign in again to continue.",
  [ApiErrorCode.AUTH_TURNSTILE_VERIFICATION_FAILED]:
    "Verification failed. Please complete the security check and try again.",
  [ApiErrorCode.INVALID_TOTP_TOKEN]: "Invalid TOTP token",
  [ApiErrorCode.PASSKEY_REGISTRATION_CANCELLED]: "Passkey setup was cancelled.",
  [ApiErrorCode.PASSKEY_VERIFICATION_FAILED]: "Passkey verification failed",
  [ApiErrorCode.SESSION_EXPIRED]: "TOTP session expired",
  [ApiErrorCode.TEMPORARY_LOCKOUT]: "Account temporarily locked",
  [ApiErrorCode.INVALID_PASSWORD]: "Invalid current password",
  // User (user.dictionary.ts) – USER_NOT_FOUND: do not use backend "User not found"; leaks account existence
  [ApiErrorCode.EMAIL_ALREADY_VERIFIED]: "User is already verified",
  [ApiErrorCode.USER_NOT_FOUND]: "Invalid email or password",
  [ApiErrorCode.USER_ALREADY_VERIFIED]: "User already verified",
  [ApiErrorCode.ALREADY_VERIFIED]: "Email is already verified",
  [ApiErrorCode.CODE_EXPIRED]: "Verification code has expired",
  [ApiErrorCode.EMAIL_ALREADY_EXISTS]: "Email address is already in use",
  [ApiErrorCode.INVALID_CODE]: "Invalid verification code",
  [ApiErrorCode.NO_PASSWORD_SET]: "No password set for this account",
  [ApiErrorCode.NO_PENDING_REQUEST]: "No pending email change request found",
  [ApiErrorCode.PENDING_EMAIL_CHANGE_EXISTS]: "User already has a pending email change",
  [ApiErrorCode.ACCOUNT_ALREADY_ACTIVE]: "Account already active",
  [ApiErrorCode.ACCOUNT_ALREADY_DEACTIVATED]: "Account already deactivated",
  [ApiErrorCode.ACCOUNT_DEACTIVATED]:
    "Your account is deactivated. Reactivate from the link in your email or contact customer.success@rivetai.com.",
  [ApiErrorCode.ACCOUNT_PENDING_DELETION]: "Your account is scheduled for deletion.",
  [ApiErrorCode.SSO_REQUIRED_FOR_DOMAIN]:
    "Your organization signs in with single sign-on. Use Sign in to organization.",
  [ApiErrorCode.CANNOT_ACTIVATE_FREE_PLAN]: "Cannot activate free plan",
  [ApiErrorCode.CANNOT_DEACTIVATE_FREE_PLAN]: "Cannot deactivate free plan",
  [ApiErrorCode.INVITATION_NOT_FOUND]: "Invitation not found or processed",
  [ApiErrorCode.INVITATION_DUPLICATE]: "User already has an existing invitation",
  [ApiErrorCode.INVITE_USER_MISMATCH]: "Invite was issued to a different account",
  [ApiErrorCode.NO_OPEN_SEAT]: "No open seat for this user",
  [ApiErrorCode.SELF_INVITE]: "Cannot invite yourself",
  [ApiErrorCode.INVITATION_NOT_PENDING]: "Invitation is not pending",
  [ApiErrorCode.INVITATION_EXPIRED]:
    "This invitation has expired. Ask the workspace for a new one.",
  [ApiErrorCode.WORKSPACE_OWNER_MEMBERSHIP_LOCKED]:
    "The workspace owner's role and access cannot be changed",
  [ApiErrorCode.WORKSPACE_MEMBER_SELF_CHANGE_NOT_ALLOWED]:
    "You cannot change your own role or access",
  [ApiErrorCode.WORKSPACE_ADMIN_CHANGE_REQUIRES_OWNER]:
    "Only the workspace owner can change or remove an admin",
  [ApiErrorCode.WORKSPACE_MEMBER_CHANGE_NOT_ALLOWED]:
    "You cannot manage a member who has workspace permissions you do not have",
  [ApiErrorCode.WORKSPACE_ROLE_GRANT_NOT_ALLOWED]:
    "You cannot assign a role with permissions you do not have",
  [ApiErrorCode.PROJECT_CREATOR_ROLE_CANNOT_ASSIGN]: "The project creator role cannot be assigned",
  [ApiErrorCode.INVALID_EMAIL_DOMAIN]: "Cannot use this email domain",
  [ApiErrorCode.CANNOT_EXTEND_PLAN]: "Cannot extend this plan",
  [ApiErrorCode.NO_SUBSCRIPTION]: "User does not have a subscription",
  [ApiErrorCode.INVALID_EXPIRY_DATE]: "New expiry date must come after old",
  [ApiErrorCode.SEAT_LIMIT_REACHED]: "Seat limit for subscription reached",
  [ApiErrorCode.NOT_ORGANIZATION_MEMBER]: "User does not belong to the organization",
  [ApiErrorCode.PRIMARY_ACCOUNT_ALREADY_REGISTERED]: "Primary account already registered",
  [ApiErrorCode.PROJECT_ACCESS_REQUIRED]: "Provide ID or grant all project access",
  [ApiErrorCode.PROJECT_REQUIRED]: "Project is required",
  [ApiErrorCode.PROJECT_TEAM_LIMIT_REACHED]: "Project team member limit reached",
  [ApiErrorCode.PROJECT_SEAT_CAP_REACHED]: "Project seat limit reached for this license tier",
  [ApiErrorCode.USER_ALREADY_ACTIVE]: "User already active",
  [ApiErrorCode.USER_ALREADY_MEMBER]: "User is already a member of workspace",
  [ApiErrorCode.USER_ALREADY_REVOKED]: "User already revoked",
  [ApiErrorCode.PENDING_INVITATION_EXISTS]: "User already has a pending invitation",
  // Workspace (workspace.dictionary.ts)
  [ApiErrorCode.INVALID_INVITATION_TOKEN]: "Invalid or expired invitation token",
  [ApiErrorCode.CANNOT_REMOVE_OWNER]: "Cannot remove workspace owner",
  [ApiErrorCode.OWNER_ROLE_CANNOT_ASSIGN]: "Owner role cannot be assigned via API",
  [ApiErrorCode.PROJECT_NOT_IN_WORKSPACE]: "Project does not exist in workspace",
  [ApiErrorCode.WORKSPACE_ADMIN_REQUIRED]: "Workspace owner or admin required",
  [ApiErrorCode.WORKSPACE_NAME_EMPTY]: "Workspace name cannot be empty",
  [ApiErrorCode.WORKSPACE_NOT_FOUND]: "Workspace not found",
  [ApiErrorCode.NOT_WORKSPACE_MEMBER]: "User is not a member of workspace",
  [ApiErrorCode.WORKSPACE_MEMBER_LIMIT_REACHED]:
    "This workspace has reached the maximum number of members and pending invitations",
  [ApiErrorCode.CLAUDE_CONNECTOR_LICENSE_REQUIRED]:
    "A Studio-licensed project or an active plan is required to connect Claude.",
  [ApiErrorCode.MCP_BASE_URL_NOT_CONFIGURED]:
    "AI app connections are not configured in this environment.",
  // Project (project.dictionary.ts)
  [ApiErrorCode.PROJECT_LIMIT_REACHED]: "Project limit reached",
  [ApiErrorCode.PROJECT_NOT_FOUND]: "Project not found",
  [ApiErrorCode.NO_FILE_UPLOADED]: "No file uploaded",
  [ApiErrorCode.NO_PRICING_PLAN]: "No pricing plan found for user",
  [ApiErrorCode.UNLICENSED_MAX_DRAFT_SCENES_REACHED]:
    "Unlicensed projects are limited to the maximum number of scenes allowed on your workspace plan. Purchase a project license or upgrade your plan to add more scenes.",
  // Payment (payment.dictionary.ts)
  [ApiErrorCode.CUSTOMER_ID_OR_EMAIL_REQUIRED]: "Customer id or email missing",
  [ApiErrorCode.NOT_ENOUGH_PROJECTS]: "Cannot downgrade projects",
  [ApiErrorCode.NOT_ENOUGH_SEATS]: "Cannot downgrade seats",
  [ApiErrorCode.SUBSCRIPTION_ALREADY_CANCELLED]: "Subscription already cancelled",
  [ApiErrorCode.INSUFFICIENT_AI_CREDITS]: "Your AI credit balance is too low for this action",
  // Budgeting / scheduling (scheduling.dictionary.ts)
  [ApiErrorCode.BUDGET_RERUN_DISALLOWED]: "Rerun not allowed",
  [ApiErrorCode.BUDGET_VERSION_LIMIT_REACHED]: "Limit for saved budget versions reached",
  [ApiErrorCode.BUDGET_LOCKED]:
    "This estimate is locked. Unlock it to change structure, rates, or assumptions.",
  [ApiErrorCode.BUDGET_RESTORE_BLOCKED_BY_ACTUALS]: "Cannot replace a live budget that has actuals",
  [ApiErrorCode.BUDGET_RESTORE_BLOCKED_WHEN_LOCKED]:
    "Cannot replace the live budget while its estimate is locked",
  [ApiErrorCode.DAYBREAK_LOCKED]: "This shoot day is locked",
  [ApiErrorCode.STRIPBOARD_ARCHIVED]: "Stripboard row already in boneyard",
  [ApiErrorCode.STRIPBOARD_INDEX_TAKEN]: "Index already taken",
  // Same meaning as the API text, but in the app's terms: the UI calls day breaks shoot days.
  [ApiErrorCode.BUDGET_STRIPBOARD_SHOOT_TIME_UNAVAILABLE]:
    "The stripboard has no shoot days. Add a shoot day to the stripboard, or turn off stripboard shoot time and set a shoot length, then try again. No credits were used.",
  // Tax (tax.dictionary.ts)
  [ApiErrorCode.ERROR_ADDING_TAX_INCENTIVE_TYPE]: "Error adding tax incentive type",
  [ApiErrorCode.ERROR_CREATING_TAX_INCENTIVE]: "Error creating tax incentive",
  [ApiErrorCode.ERROR_FETCHING_TAX_INCENTIVE]: "Error fetching tax incentives",
  [ApiErrorCode.ERROR_FETCHING_TAX_INCENTIVE_TYPES]: "Error fetching tax incentive types",
  // Common (common.dictionary.ts)
  [ApiErrorCode.RATE_LIMITED]: "Too many attempts; please try again later",
  [ApiErrorCode.AI_API_ERROR]: "OpenAI/LLM API error",
  [ApiErrorCode.INTERNAL_SERVER_ERROR]: "An internal error occurred.",
  [ApiErrorCode.MAINTENANCE]: "Service is under maintenance.",
};
